import { defineStore } from 'pinia';

import { store } from '../index_SignIn';

import axios from "axios";

const LOCALE_STORAGE_KEY = 'fap5.locale';
const SUPPORTED_LOCALES = ['zh-CN', 'en-US'] as const;

type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const normalizeLocale = (locale?: string | null): SupportedLocale | null => {
  if (!locale) {
    return null;
  }

  const trimmed = locale.trim();
  if ((SUPPORTED_LOCALES as readonly string[]).includes(trimmed)) {
    return trimmed as SupportedLocale;
  }

  if (trimmed.toLowerCase().startsWith('zh')) {
    return 'zh-CN';
  }

  if (trimmed.toLowerCase().startsWith('en')) {
    return 'en-US';
  }

  return null;
};

const getLocaleFromBrowser = (): SupportedLocale => {
  const browserLocale = navigator.language?.trim().toLowerCase() || '';
  if (browserLocale.startsWith('zh')) {
    return 'zh-CN';
  }

  return 'en-US';
};

const getInitialLocale = (): SupportedLocale => {
  const cachedLocale = normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
  if (cachedLocale) {
    return cachedLocale;
  }

  return getLocaleFromBrowser();
};

// 声明全局变量类型
declare global {
  interface Window {
    bgImgsInterval?: number;
  }
}

export const useAppSettingStore = defineStore('appsetting', {
  state: () => ({
    loading: true,
    name: '',
    title: '',
    ico: '',
    logoImg: '',
    sloganImg: '',
    enableWXQRLogin: false,
    enableWXNativeAppLogin: false,
    enableQYWXQRLogin: false,
    enableAccountLogin: false,
    enableAlipayLogin: false,
    enableDingTalkLogin: false,
    enableForgetPassword: false,
    forgetPasswordUrl: '',
    showDefaultPasswordTip: false,
    bgImgs: [],
    signInCaptchaMode: '1', //登录验证码方式，1为不验证，2为自生验证码，3为腾讯验证码
    weixinCorpAgentId: '',
    weixinCorpCssUrl: '',
    weixinCorpId: '',
    wxAppId: '',
    wxCssUrl: '',
    themeColor: 'red', // 主题颜色
    copyRight: '',
    indexCss: '',
    enableRecoveryEmailOnLogin: false, // 是否启用登录时密保邮箱验证
    currentBgIndex: 0, // 新增：当前背景图片索引
    currentLocale: getInitialLocale(),
  }),
  getters: {
    showSignInModeSelector(state) {
      const loginMethods = [
        state.enableWXQRLogin || state.enableWXNativeAppLogin,
        state.enableQYWXQRLogin,
        state.enableAccountLogin,
        state.enableDingTalkLogin,
        state.enableAlipayLogin
      ];

      const enabledCount = loginMethods.filter(method => method === true).length;
      return enabledCount >= 2;
    },
    // returnUrl() {
    //   var result = location.search.match(new RegExp("[\?\&]ReturnUrl=([^\&]+)", "i"));
    //   if (result == null || result.length < 1) {
    //     return '';
    //   }

    //   return result[1];
    // }
    currentBgImg(state) {
      if (state.bgImgs && state.bgImgs.length > 0) {
        return state.bgImgs[state.currentBgIndex];
      }
      return '';
    }
  },
  actions: {
    async getSignInConfig() {
      this.loading = true;

      const response = await axios.get('api/Config/GetSignInConfig').catch(function (error) {
        console.log(error);
      });

      var signInConfig = response.data.result;

      document.title = signInConfig.title;
      let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }

      link.href = signInConfig.ico;

      this.name = signInConfig.name;
      this.title = signInConfig.title;
      this.ico = signInConfig.ico;
      this.logoImg = signInConfig.logoImg ? signInConfig.logoImg : '';
      this.sloganImg = signInConfig.sloganImg ? signInConfig.sloganImg : '';
      this.enableWXQRLogin = signInConfig.enableWXQRLogin ? signInConfig.enableWXQRLogin : false;
      this.enableWXNativeAppLogin = signInConfig.enableWXNativeAppLogin ? signInConfig.enableWXNativeAppLogin : false;
      this.enableQYWXQRLogin = signInConfig.enableQYWXQRLogin ? signInConfig.enableQYWXQRLogin : false;
      this.enableAccountLogin = signInConfig.enableAccountLogin ? signInConfig.enableAccountLogin : false;
      this.enableAlipayLogin = signInConfig.enableAlipayLogin ? signInConfig.enableAlipayLogin : false;
      this.enableDingTalkLogin = signInConfig.enableDingTalkLogin ? signInConfig.enableDingTalkLogin : false;
      this.enableForgetPassword = signInConfig.enableForgetPassword ? signInConfig.enableForgetPassword : false;
      this.forgetPasswordUrl = signInConfig.forgetPasswordUrl ? signInConfig.forgetPasswordUrl : '';
      this.showDefaultPasswordTip = signInConfig.showDefaultPasswordTip ? signInConfig.showDefaultPasswordTip : false;
      this.bgImgs = signInConfig.bgImgs ? signInConfig.bgImgs : [];
      this.signInCaptchaMode = signInConfig.signInCaptchaMode ? signInConfig.signInCaptchaMode : '1';
      this.weixinCorpAgentId = signInConfig.weixinCorpAgentId ? signInConfig.weixinCorpAgentId : '';
      this.weixinCorpCssUrl = signInConfig.weixinCorpCssUrl ? signInConfig.weixinCorpCssUrl : '';
      this.weixinCorpId = signInConfig.weixinCorpId ? signInConfig.weixinCorpId : '';
      this.wxAppId = signInConfig.wxAppId ? signInConfig.wxAppId : '';
      this.wxCssUrl = signInConfig.wxCssUrl ? signInConfig.wxCssUrl : '';
      this.themeColor = signInConfig.themeColor ? signInConfig.themeColor : 'red';
      this.copyRight = signInConfig.copyRight ? signInConfig.copyRight : '';
      this.indexCss = signInConfig.indexCss ? signInConfig.indexCss : '';
      this.enableRecoveryEmailOnLogin = signInConfig.enableRecoveryEmailOnLogin ? signInConfig.enableRecoveryEmailOnLogin : false;

      // ...existing code for other properties...
      this.bgImgs = signInConfig.bgImgs ? signInConfig.bgImgs : [];

      // 移除原来的 body 背景设置逻辑，只保留初始索引设置
      this.currentBgIndex = 0;

      document.documentElement.setAttribute('theme-color', this.themeColor);

      this.loading = false;
    },
    nextBgImg() {
      if (this.bgImgs && this.bgImgs.length > 1) {
        this.currentBgIndex = (this.currentBgIndex + 1) % this.bgImgs.length;
      }
    },
    loadScript(src: string, name: string, checkPattern?: string) {
      // 使用传入的检查模式或默认从URL中提取文件名
      const pattern = checkPattern || src.split('/').pop()?.split('?')[0] || '';

      // 检查脚本是否已经加载
      if (document.querySelector(`script[src*="${pattern}"]`)) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        script.onload = () => {
          console.log(`${name}脚本加载成功`);
          resolve(true);
        };

        script.onerror = () => {
          console.error(`${name}脚本加载失败`);
          reject(new Error(`Failed to load ${name} script`));
        };

        document.head.appendChild(script);
      });
    },
    loadDingTalkScript() {
      return this.loadScript(
        'https://g.alicdn.com/dingding/h5-dingtalk-login/0.21.0/ddlogin.js',
        '钉钉登录',
        'ddlogin.js'
      );
    },
    loadTencentCaptchaScript() {
      return this.loadScript(
        'https://turing.captcha.qcloud.com/TJCaptcha.js',
        '腾讯验证码',
        'TJCaptcha.js'
      );
    },
    setCurrentLocale(locale: string) {
      const nextLocale = normalizeLocale(locale) || 'en-US';
      this.currentLocale = nextLocale;
      localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    }
  },
});

export function getAppSettingStore() {
  return useAppSettingStore(store);
}
